import { computed, inject, Service, signal } from '@angular/core';
import { Router } from '@angular/router';
import { finalize, map, Observable, shareReplay, tap } from 'rxjs';

import { ApiService } from '../api/api.service';
import { AdminUser, OtpSent, WebSession, WebTokens } from './admin-user';

const REFRESH_KEY = 'bcc.admin.refresh';
const USER_KEY = 'bcc.admin.user';

/**
 * Session de l'admin web (JWT). L'access token (15 min) reste en mémoire ; le
 * refresh token (7 j, rotation + révocation côté serveur) est gardé dans le
 * navigateur pour survivre à un rechargement.
 */
@Service()
export class AuthStore {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);

  private readonly accessToken = signal<string | null>(null);
  private readonly refreshToken = signal<string | null>(read(REFRESH_KEY));
  readonly user = signal<AdminUser | null>(parse<AdminUser>(read(USER_KEY)));
  readonly isLoggedIn = computed(() => this.refreshToken() !== null);

  private refreshing$: Observable<string> | null = null;

  get access(): string | null {
    return this.accessToken();
  }

  requestCode(phone: string): Observable<OtpSent> {
    return this.api.post<OtpSent>('auth/login/', { phone });
  }

  resendCode(phone: string): Observable<OtpSent> {
    return this.api.post<OtpSent>('auth/otp/resend/', { phone, otp_type: 'LOGIN' });
  }

  verify(phone: string, code: string): Observable<AdminUser> {
    return this.api.post<WebSession>('web/auth/otp/verify/', { phone, code, otp_type: 'LOGIN' }).pipe(
      tap((session) => this.store(session, session.user)),
      map((session) => session.user),
    );
  }

  /** Un seul rafraîchissement à la fois : les requêtes en 401 attendent le même. */
  refresh(): Observable<string> {
    const refresh = this.refreshToken();
    if (!refresh) {
      throw new Error('Pas de session');
    }
    this.refreshing$ ??= this.api.post<WebTokens>('web/auth/refresh/', { refresh }).pipe(
      tap((tokens) => this.store(tokens)),
      map((tokens) => tokens.access),
      finalize(() => (this.refreshing$ = null)),
      shareReplay(1),
    );
    return this.refreshing$;
  }

  logout(): void {
    const refresh = this.refreshToken();
    if (refresh && this.accessToken()) {
      this.api.post('web/auth/logout/', { refresh }).subscribe({ error: () => undefined });
    }
    this.clear();
  }

  /** Session perdue (refresh expiré ou révoqué) : retour à la connexion. */
  clear(): void {
    this.accessToken.set(null);
    this.refreshToken.set(null);
    this.user.set(null);
    remove(REFRESH_KEY);
    remove(USER_KEY);
    void this.router.navigate(['/connexion']);
  }

  private store(tokens: WebTokens, user?: AdminUser): void {
    this.accessToken.set(tokens.access);
    this.refreshToken.set(tokens.refresh);
    write(REFRESH_KEY, tokens.refresh);
    if (user) {
      this.user.set(user);
      write(USER_KEY, JSON.stringify(user));
    }
  }
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* stockage indisponible : la session ne survivra pas au rechargement */
  }
}

function remove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* rien à faire */
  }
}

function parse<T>(value: string | null): T | null {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}
